import { MoreVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { PublicUser, RoleEnum, WithPagination } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { useRemoveUserFromOrg } from "../api/remove-user-from-org";

type UserRowActionsProps = {
  userId: string;
  organisationId: string;
};

/**
 * This component is used to REMOVE a user FROM an organisation.
 * Its used for listing users as a regular admin.
 * This is NOT used for SUPERADMIN
 * For SUPERADMIN, check out user-sa-row-actions.tsx
 */
export function UserRowActions(props: UserRowActionsProps) {
  const {
    mutateAsync: removeUserFromOrg,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useRemoveUserFromOrg({
    onSuccess: () => {
      queryClient.setQueriesData(
        { queryKey: ["users", props.organisationId], exact: false },
        (oldData: WithPagination<PublicUser> | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            items: oldData.items.filter((user) => user.id !== props.userId),
          };
        },
      );
    },
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    try {
      await removeUserFromOrg({
        userId: props.userId,
        orgId: props.organisationId,
      });
      queryClient.invalidateQueries({
        queryKey: ["users", props.organisationId],
      });
      setIsDeleting(false);
    } catch (error) {
      console.error(error);
    }
  };

  const { hasPermission } = useOrganisation();

  return (
    <Dialog onOpenChange={setIsDeleting} open={isDeleting}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {hasPermission(RoleEnum.USER_MANAGEMENT) && (
            <DropdownMenuItem asChild>
              <Link to={`/settings/users/${props.userId}`}>Edit</Link>
            </DropdownMenuItem>
          )}
          <DialogTrigger asChild>
            <DropdownMenuItem className="text-destructive">
              Delete
            </DropdownMenuItem>
          </DialogTrigger>
        </DropdownMenuContent>
      </DropdownMenu>

      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to remove this user from your organisation?
          </DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>
        {isError && <ErrorMessage error={error} />}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
